import PropTypes from 'prop-types';
import { Grid, IconButton, MenuItem, Paper, TextField, Tooltip } from '@mui/material';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { AUTO_SORTS, getSortMode, slugify } from './filterConfig';

const MANUAL = '__manual__';

// Manual sort values come from the label; never reuse an auto key
const manualValue = (label) => {
  const slug = slugify(label);
  return AUTO_SORTS.some((s) => s.value === slug) ? `m_${slug}` : slug;
};

// Options for a Sort filter. Each row is a label plus what it sorts by:
// an automatic sort (price / number of products) or "Ticked packs first",
// where packs are ticked for that option in the value pack form.
const SortOptionsEditor = ({ options, onChange }) => {
  const update = (index, patch) =>
    onChange(options.map((option, i) => (i === index ? { ...option, ...patch } : option)));

  const handleLabel = (index, label) => {
    const option = options[index];
    update(index, getSortMode(option) === 'manual' ? { label, value: manualValue(label) } : { label });
  };

  const handleSortsBy = (index, choice) => {
    const option = options[index];
    if (choice === MANUAL) {
      update(index, { mode: 'manual', value: manualValue(option.label || 'custom') });
    } else {
      const auto = AUTO_SORTS.find((s) => s.value === choice);
      update(index, { mode: 'auto', value: choice, label: option.label || auto?.label || '' });
    }
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <MDBox>
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        The first option is the default. &quot;Ticked packs first&quot; options show up as checkboxes in the
        value pack form, packs ticked for one are listed first when a shopper picks it.
      </MDTypography>

      {options.map((option, index) => {
        const mode = getSortMode(option);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Paper key={index} variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Label"
                  value={option.label}
                  onChange={(e) => handleLabel(index, e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Sorts by"
                  value={mode === 'manual' ? MANUAL : option.value}
                  onChange={(e) => handleSortsBy(index, e.target.value)}
                  SelectProps={{ sx: { height: 40 } }}
                >
                  <MenuItem value={MANUAL}>Ticked packs first</MenuItem>
                  {AUTO_SORTS.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3} display="flex" justifyContent="flex-end">
                <Tooltip title="Move up">
                  <span>
                    <IconButton size="small" disabled={index === 0} onClick={() => move(index, -1)}>
                      <Icon fontSize="small">arrow_upward</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      disabled={index === options.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <Icon fontSize="small">arrow_downward</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onChange(options.filter((_, i) => i !== index))}
                  >
                    <Icon fontSize="small">delete</Icon>
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      <MDButton
        variant="outlined"
        color="info"
        size="small"
        onClick={() => onChange([...options, { value: '', label: '', mode: 'manual' }])}
      >
        <Icon sx={{ mr: 0.5 }}>add</Icon> Add sort option
      </MDButton>
    </MDBox>
  );
};

SortOptionsEditor.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string, mode: PropTypes.string }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SortOptionsEditor;
